import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Flex,
  Icon,
  Text,
  Button,
  Box,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import {
  BasicInput,
  MultiSelectInput,
} from "../../components/HookFormsInputs";
import { ReactComponent as LeftArrow } from "../../assets/icons/left-arrow.svg";
import WithAppLayout from "../../components/WithAppLayout";
import WithQuery from "../../components/WithQuery";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { EDIT_ROLE_ALL_QUERY, NEW_ROLE_ALL_QUERY, UPSERT_ROLE_MUTATION } from "../../graphql/admin";
import PageTitle from "../../components/PageTitle";
import MoblyzeApolloClient from "../../services/MoblyzeApolloClient";
import { ROLES_PATH } from "../../routes";
import AddSkillModal from "../../components/AddSkillModal";
import AddCertificationModal from "../../components/AddCertificationModal";

function CreateEditRolePage({ id, data, error, loading, refetch, currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { isOpen: isSkillOpen, onOpen: onSkillOpen, onClose: onSkillClose } = useDisclosure();
  const { isOpen: isCertOpen, onOpen: onCertOpen, onClose: onCertClose } = useDisclosure();
  const [ skillOptions, setSkillOptions ] = useState([]);
  const [ certificationOptions, setCertificationOptions ] = useState([]);

  const newRole = !id || id.toLowerCase() === "new" || location.pathname.endsWith("/copy");
  const candidateRole = data?.getCandidateRole;

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      name: candidateRole?.name || "",
      category: candidateRole?.category || "Subsea",
      subCategory: candidateRole?.subCategory || "Uncategorized",
      skillIds: candidateRole?.skillIds || [],
      certificationIds: candidateRole?.certificationIds || [],
    },
  });

  // Add this useEffect to update form values when data changes
  useEffect(() => {
    if (data?.allSkills) {
      const skillOptions = data.allSkills.map((skill) => ({
        value: skill.id,
        label: skill.name,
      }));
      setSkillOptions(skillOptions);
    }
    if (data?.allCertifications) {
      const certificationOptions = data.allCertifications.map((cert) => ({
        value: cert.id,
        label: cert.name,
      }));
      setCertificationOptions(certificationOptions);
    }
    if (data?.getCandidateRole) {
      const skillMap = data?.allSkills ? data.allSkills.reduce((acc, skill) => {
        acc[skill.id] = skill.name;
        return acc;
      }, {}) : {};
      const certificationMap = data?.allCertifications ? data.allCertifications.reduce((acc, cert) => {
        acc[cert.id] = cert.name;
        return acc;
      }, {}) : {};
      const skillIds = data.getCandidateRole?.skillIds
        ? data.getCandidateRole.skillIds.map((s) => ({ value: s, label: skillMap[s] || 'Deleted Skill' }))
        : [];
      const certificationIds = data.getCandidateRole?.certificationIds
        ? data.getCandidateRole.certificationIds.map((c) => ({ value: c, label: certificationMap[c] || 'Deleted Certification' }))
        : [];
      reset({
        ...candidateRole,
        ...{ skillIds },
        ...{ certificationIds },
      });
    }
  }, [data, reset, candidateRole]);

  if (loading) {
    return <LoadingIndicator />;
  }

  async function onSubmit(values) {
    const variables = {
      ...values,
      skillIds: values.skillIds.map((s) => s.value),
      certificationIds: values.certificationIds.map((c) => c.value),
    };

    if (newRole) {
      // If creating a new role, we don't need to pass an id
      delete variables.id;
    }
    try {
      const response = await MoblyzeApolloClient.mutate({
        mutation: UPSERT_ROLE_MUTATION,
        variables,
      });
      console.log("Response from upsert:", response);
      if (response?.data?.upsertCandidateRole) {
        // If we successfully created or updated the role, navigate back to the roles page
        navigate(ROLES_PATH);
      } else {
        // Handle error case
        console.error("Failed to upsert role:", response);
      }
    } catch (error) {
      console.error("Error during upsert:", error);
      // Handle the error, e.g., show a toast notification
    }
  }

  const certLabel = (
    <HStack spacing={2}>
      <Text fontSize="md" fontWeight="bold">Certifications</Text>
      <Button
        leftIcon={<AddIcon />}
        colorScheme="gray"
        variant="solid"
        size="xs"
        onClick={onCertOpen}
      >Add New Certification</Button>
    </HStack>
  );
  const skillLabel = (
    <HStack spacing={2}>
      <Text fontSize="md" fontWeight="bold">Skills</Text>
      <Button
        leftIcon={<AddIcon />}
        colorScheme="gray"
        variant="solid"
        size="xs"
        onClick={onSkillOpen}
      >Add New Skill</Button>
    </HStack>
  );

  return (
    <Flex h="100%" direction="column" flex="1">
      <Text
        onClick={() => {
          // if we can naviate to the previous page, do so
          // otherwise, navigate to the home page
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate("/");
          }
        }}
        fontSize="md"
        lineHeight="6"
        fontWeight="normal"
        cursor="pointer"
        mb={4}
      >
        <Icon fontSize="10px" as={LeftArrow} /> Back
      </Text>
      <Box>
        <form
          onSubmit={(event) => {
            event.stopPropagation();
            event.preventDefault();
            handleSubmit(onSubmit)(event);
          }}
        >
          <PageTitle title={`${newRole ? "Add New Role" : "Edit Role"}`} />

          <BasicInput
            label="Name"
            name="name"
            placeholder="Name of the role"
            register={register}
            control={control}
            errors={errors}
            required
          />

          <BasicInput
            label="Category"
            name="category"
            placeholder="Category of the role"
            register={register}
            control={control}
            errors={errors}
            required
          />

          <BasicInput
            label="Subcategory"
            name="subCategory"
            placeholder="Subcategory of the role"
            register={register}
            control={control}
            errors={errors}
            required
          />

          <MultiSelectInput
            name="certificationIds"
            label={certLabel}
            options={certificationOptions}
            register={register}
            control={control}
            errors={errors}
          />

          <MultiSelectInput
            name="skillIds"
            label={skillLabel}
            options={skillOptions}
            register={register}
            control={control}
            errors={errors}
          />

          <Button mt={4} isLoading={isSubmitting} type="submit">
            {newRole ? "Create New Role" : "Save Role"}
          </Button>
        </form>
      </Box>
      <AddSkillModal
        isOpen={isSkillOpen}
        onClose={onSkillClose}
        createdCallback={(newSkill) => {
          // Update the form with the new skill
          const newSkillOption = { value: newSkill.id, label: newSkill.name };
          setSkillOptions((prev) => [...prev, newSkillOption]);
          setValue("skillIds", [...getValues("skillIds"), newSkillOption]);
        }}
      />
      <AddCertificationModal
        isOpen={isCertOpen}
        onClose={onCertClose}
        createdCallback={(newCertification) => {
          // Update the form with the new certification
          const newCertificationOption = { value: newCertification.id, label: newCertification.name };
          setCertificationOptions((prev) => [...prev, newCertificationOption]);
          setValue("certificationIds", [...getValues("certificationIds"), newCertificationOption]);
        }}
      />
    </Flex>
  );
}

function PageComponent({ children }) {
  const { id } = useParams();

  if (id === "new") {
    // If the id is 'new', we are creating a new role
    return WithQuery(
      CreateEditRolePage,
      NEW_ROLE_ALL_QUERY,
      {},
      {
        id,
        children,
      }
    )();
  }

  return WithQuery(
    CreateEditRolePage,
    EDIT_ROLE_ALL_QUERY,
    { id },
    {
      id,
      children,
    }
  )();
}

export default WithAppLayout(PageComponent, {
  showGradientBackground: true,
});
